/* eslint-disable no-void */
import * as vp from 'vopay';

import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePartnerData } from '@/providers/PartnerData.provider';

const { Badge, Box, Button, Icon } = vp;

type CredentialsPanelProps = {
  credentials: vp.EncodedCredentials | vp.ApiCredentials;
};

type FC = FunctionComponent<CredentialsPanelProps>;

const CredentialsPanel: FC = ({ credentials }: CredentialsPanelProps) => {
  const {
    response: { isRoleViewOnly },
  } = usePartnerData();
  const [onClipboard, setOnClipboard] = useState<string | null>(null);
  const { t } = useTranslation('translations', { keyPrefix: 'UI.CredentialsPanel' });

  const handleCopied = (id: 'credentials') => {
    const handlers = {
      credentials: () => {
        void navigator?.clipboard?.writeText(JSON.stringify(buildCredentials(credentials)));
      },
    };

    handlers[id]();
    setOnClipboard(id);
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;

    const resetClipboard = () => {
      timer = setTimeout(() => {
        setOnClipboard(null);
      }, 1000);
    };

    if (onClipboard) resetClipboard();

    return () => {
      clearTimeout(timer);
    };
  }, [onClipboard]);

  return (
    <Box boundary="shadow" background="white" mb="lg" pt="md" pb="md" ps="md" pe="md" jc="sb">
      <Box jc="fs" ai="center" gap="md" wrap="wrap">
        <Box jc="center" ai="center" gap="md" wrap="wrap">
          <Icon icon="user" size="md" />
          {t('accountID')}
          {credentials.AccountID && <Badge label={credentials.AccountID} />}
        </Box>
        <Box jc="center" ai="center" gap="md" wrap="wrap">
          <Icon icon="development" size="md" />
          {t('apiKey')}
          {credentials.ApiKey && <Badge label={credentials.ApiKey} />}
        </Box>
        <Box jc="center" ai="center" gap="md" wrap="wrap">
          <Icon icon="lock" size="md" />
          {getSecretLabel()}
          {getSecretValue() && <Badge label={getSecretValue()} />}
        </Box>
      </Box>
      <Button
        id="copy-credentials"
        label={onClipboard === 'credentials' ? t('copiedButton') : t('copyButton')}
        width="140px"
        onClick={() => handleCopied('credentials')}
        color="outlined"
        isDisabled={isRoleViewOnly}
      />
    </Box>
  );

  function buildCredentials(creds: vp.EncodedCredentials | vp.ApiCredentials) {
    const baseCredentials = { accountId: creds.AccountID, apiKey: creds.ApiKey };

    if (isEncodedCredentials(creds)) {
      return { ...baseCredentials, apiSharedSecret: creds.ApiSharedSecret };
    }

    if (isApiCredentials(creds)) {
      return { ...baseCredentials, apiSignature: creds.ApiSignature };
    }

    return baseCredentials;
  }

  function isEncodedCredentials(creds: unknown): creds is vp.EncodedCredentials {
    return Object.prototype.hasOwnProperty.call(creds, 'ApiSharedSecret');
  }

  function isApiCredentials(creds: unknown): creds is vp.ApiCredentials {
    return Object.prototype.hasOwnProperty.call(creds, 'ApiSignature');
  }

  function getSecretLabel() {
    if (isEncodedCredentials(credentials)) return t('apiSharedSecret');
    if (isApiCredentials(credentials)) return t('apiSignature');

    return '';
  }

  function getSecretValue() {
    if (isEncodedCredentials(credentials)) return credentials.ApiSharedSecret;
    if (isApiCredentials(credentials)) return credentials.ApiSignature;

    return '';
  }
};

export default CredentialsPanel;
